import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@busbook/ui';
import { TenantProvider, useTenantBoot } from './lib/tenant.js';
import { AppShell } from './components/nav/AppShell.js';
import { AppUpdater } from './components/AppUpdater.js';
import { RequireCustomer } from './components/RequireCustomer.js';
import {
  NotLiveScreen,
  OfflineBootScreen,
  SuspendedScreen,
  UnknownHostScreen,
} from './components/Maintenance.js';
import { usePreviewBranding } from './lib/preview.js';
import { Home } from './screens/Home.js';
import { Results } from './screens/Results.js';
import { SeatSelection } from './screens/SeatSelection.js';
import { Checkout } from './screens/Checkout.js';
import { Ticket } from './screens/Ticket.js';
import { MyTrips } from './screens/trips/MyTrips.js';
import { Travellers } from './screens/Travellers.js';
import { Profile } from './screens/Profile.js';
import { Offers } from './screens/Offers.js';
import { Dispute } from './screens/Dispute.js';
import { Help, About } from './screens/Help.js';
import { ForgotPassword, Register, ResetPassword, SignIn } from './screens/Auth.js';
import { CustomerSessionProvider } from './lib/customer-session.js';
import { LocaleProvider } from './i18n/index.js';

export function App() {
  const state = useTenantBoot();
  // Draft identity from the branding editor, laid over the published branding rather than
  // replacing it — the editor owns name, logo, colours and type, not navigation.
  const preview = usePreviewBranding(state.status === 'ready' ? state.context.branding : null);

  if (state.status === 'loading') {
    return (
      <div className="grid min-h-full place-items-center">
        <span
          role="status"
          aria-label="Loading"
          className="h-6 w-6 animate-spin rounded-full border-2 border-black/15 border-t-black/45"
        />
      </div>
    );
  }
  if (state.status === 'suspended') return <SuspendedScreen {...state} />;
  if (state.status === 'not-live') return <NotLiveScreen {...state} />;
  if (state.status === 'unknown-host') return <UnknownHostScreen />;
  if (state.status === 'offline') return <OfflineBootScreen />;

  // A draft pushed by the branding editor's preview frame overrides the published
  // branding for this render only; nothing is persisted (see lib/preview.ts).
  const branding = preview ?? state.context.branding;

  return (
    <TenantProvider context={{ ...state.context, branding }}>
      {/* Seeded from the operator's configured passenger language; a passenger who
          picks their own overrides it for good. See lib/chooseLocale.ts. */}
      <LocaleProvider tenantDefault={state.context.settings.defaultLocale}>
      <ThemeProvider branding={branding} applyDocumentIdentity>
        <CustomerSessionProvider>
        <BrowserRouter>
          {/* Renders nothing; reloads the app once a newly deployed worker takes over. */}
          <AppUpdater />
          {/* The frame — brand, navigation, account — in whichever of the five shapes this
              tenant was sold. It also declares --app-bottom-inset, which screens with their
              own fixed action bar (SeatSelection) measure themselves against. */}
          <AppShell style={branding.menuStyle} items={branding.menuItems}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/results" element={<Results />} />
                {/* Search is open to everyone; taking a seat is not (DECISIONS D68). The
                    guard carries the chosen trip through sign-in and lands back on it. */}
                <Route
                  path="/trip/:id/seats"
                  element={
                    <RequireCustomer>
                      <SeatSelection />
                    </RequireCustomer>
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <RequireCustomer>
                      <Checkout />
                    </RequireCustomer>
                  }
                />
                <Route path="/booking/:reference" element={<Ticket />} />
                <Route path="/bookings" element={<MyTrips />} />
                <Route path="/travellers" element={<Travellers />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/offers" element={<Offers />} />
                <Route path="/bookings/:reference/dispute" element={<Dispute />} />
                <Route path="/help" element={<Help />} />
                <Route path="/auth/login" element={<SignIn />} />
                <Route path="/auth/register" element={<Register />} />
                <Route path="/auth/forgot" element={<ForgotPassword />} />
                <Route path="/auth/reset" element={<ResetPassword />} />
                <Route path="/about" element={<About />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
          </AppShell>
        </BrowserRouter>
        </CustomerSessionProvider>
      </ThemeProvider>
      </LocaleProvider>
    </TenantProvider>
  );
}
